import type { SupabaseClient } from '@supabase/supabase-js'
import { auditService } from './audit'

/**
 * Portal types for different applications
 */
export type PortalType = 'employer' | 'broker' | 'employee' | 'therapist' | 'client'

/**
 * Portal authentication result
 */
export interface PortalAuthResult {
  success: boolean
  user: {
    id: string
    email: string
  } | null
  session: any | null
  portalData: Record<string, any> | null
  error: string | null
  hasMFA?: boolean
  mfaFactorId?: string
  mfaChallengeId?: string
}

/**
 * Server-side portal authentication service
 *
 * This service is designed to be called from API routes (server-side)
 * for SOC2 compliance. It:
 * 1. Authenticates via Supabase Auth
 * 2. Verifies user exists in `users` table
 * 3. Checks portal-specific access:
 *    - broker: `brokers` table (userId)
 *    - employer: `employer_org_users` table (userId)
 *    - employee: `employer_members` table (employees + dependents)
 *    - therapist: `pros` table (userId or email)
 *    - client: just needs `users` record
 * 4. Logs all auth attempts for audit trail
 */
export const portalAuthService = {
  /**
   * Sign in to a specific portal (server-side)
   *
   * @param supabase - Server-side Supabase client (from createClient in API route)
   * @param adminClient - Admin Supabase client for audit logging
   * @param email - User's email
   * @param password - User's password
   * @param portal - Which portal they're signing into
   * @param ipAddress - Client IP address for audit logging
   * @returns PortalAuthResult with user data or error
   */
  async signIn(
    supabase: SupabaseClient,
    adminClient: SupabaseClient,
    email: string,
    password: string,
    portal: PortalType,
    ipAddress?: string
  ): Promise<PortalAuthResult> {
    // Step 1: Authenticate with Supabase Auth
    const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
      email,
      password,
    })

    if (authError) {
      // Log failed login for SOC2 compliance
      await this.logLoginFailed(adminClient, email, portal, authError.message, ipAddress)

      // Provide user-friendly error messages
      let userFriendlyError = authError.message
      if (authError.message === 'Invalid login credentials') {
        userFriendlyError = 'Incorrect email or password. Please check your credentials and try again.'
      } else if (authError.message.includes('Email not confirmed')) {
        userFriendlyError = 'Please verify your email address before logging in. Check your inbox for a confirmation link.'
      } else if (authError.message.includes('Invalid email')) {
        userFriendlyError = 'Please enter a valid email address.'
      } else if (authError.message.includes('rate limit') || authError.message.includes('too many')) {
        userFriendlyError = 'Too many login attempts. Please wait a few minutes and try again.'
      }

      console.error('[PortalAuth] Auth error details:', {
        message: authError.message,
        status: authError.status,
        code: (authError as any).code,
        email
      })

      return {
        success: false,
        user: null,
        session: null,
        portalData: null,
        error: userFriendlyError
      }
    }

    if (!authData.user) {
      await this.logLoginFailed(adminClient, email, portal, 'No user returned', ipAddress)

      return {
        success: false,
        user: null,
        session: null,
        portalData: null,
        error: 'Authentication failed'
      }
    }

    const userId = authData.user.id
    const userEmail = authData.user.email!

    // Step 2: Check MFA status
    const { data: mfaData } = await supabase.auth.mfa.listFactors()
    const verifiedFactors = mfaData?.totp?.filter(f => f.status === 'verified') || []
    const hasMFA = verifiedFactors.length > 0

    let mfaChallengeId: string | undefined
    if (hasMFA && verifiedFactors[0]) {
      const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId: verifiedFactors[0].id
      })
      if (!challengeError && challengeData) {
        mfaChallengeId = challengeData.id
      }
    }

    // Step 3: Verify user exists in users table (using admin client to bypass RLS)
    const { data: userData, error: userError } = await adminClient
      .from('users')
      .select('id, email, firstName, lastName')
      .eq('id', userId)
      .maybeSingle()

    if (userError) {
      console.error('[PortalAuth] Error checking users table:', userError)
    }

    // For client portal, just having a user record is enough
    if (portal === 'client') {
      if (!userData) {
        await supabase.auth.signOut()
        await this.logLoginFailed(adminClient, email, portal, 'User record not found', ipAddress)

        return {
          success: false,
          user: null,
          session: null,
          portalData: null,
          error: 'Account not found. Please sign up first.'
        }
      }

      // Log successful login
      await this.logLoginSuccess(adminClient, userId, email, portal, ipAddress)

      return {
        success: true,
        user: { id: userId, email: userEmail },
        session: authData.session,
        portalData: userData,
        error: null,
        hasMFA,
        mfaFactorId: verifiedFactors[0]?.id,
        mfaChallengeId
      }
    }

    // Step 4: Check portal-specific access (using admin client to bypass RLS)
    const accessResult = await this.checkPortalAccess(adminClient, userId, userEmail, portal)

    if (!accessResult.hasAccess) {
      await supabase.auth.signOut()
      await this.logLoginFailed(adminClient, email, portal, accessResult.error, ipAddress)

      return {
        success: false,
        user: null,
        session: null,
        portalData: null,
        error: accessResult.error
      }
    }

    // Step 5: Log successful login
    await this.logLoginSuccess(adminClient, userId, email, portal, ipAddress)

    return {
      success: true,
      user: { id: userId, email: userEmail },
      session: authData.session,
      portalData: accessResult.data,
      error: null,
      hasMFA,
      mfaFactorId: verifiedFactors[0]?.id,
      mfaChallengeId
    }
  },

  /**
   * Check if user has access to a specific portal
   */
  async checkPortalAccess(
    adminClient: SupabaseClient,
    userId: string,
    email: string,
    portal: PortalType
  ): Promise<{ hasAccess: boolean; data: Record<string, any> | null; error: string }> {
    switch (portal) {
      case 'broker': {
        // Check brokers table - userId must match
        const { data, error } = await adminClient
          .from('brokers')
          .select('id, userId, createdAt')
          .eq('userId', userId)
          .maybeSingle()

        if (error) {
          console.error('[PortalAuth] Broker check error:', error)
          return {
            hasAccess: false,
            data: null,
            error: 'Error verifying broker access'
          }
        }

        if (!data) {
          return {
            hasAccess: false,
            data: null,
            error: "You don't have access to the broker portal. Please contact support."
          }
        }

        return { hasAccess: true, data, error: '' }
      }

      case 'employer': {
        // First, check for any pending invites by email and auto-accept them
        // This ensures invites are accepted even if user already has access to other orgs
        const { data: pendingInvites, error: inviteError } = await adminClient
          .from('employer_org_users')
          .select('id, orgId, role, invitedEmail')
          .ilike('invitedEmail', email)
          .is('userId', null)

        if (inviteError) {
          console.error('[PortalAuth] Employer invite check error:', inviteError)
        }

        // Auto-accept all pending invites for this user
        if (pendingInvites && pendingInvites.length > 0) {
          for (const invite of pendingInvites) {
            const { error: updateError } = await adminClient
              .from('employer_org_users')
              .update({
                userId: userId,
                acceptedAt: new Date().toISOString()
              })
              .eq('id', invite.id)

            if (updateError) {
              console.error('[PortalAuth] Auto-accept invite error:', updateError)
            } else {
              console.log(`[PortalAuth] Auto-accepted invite for ${email} to org ${invite.orgId}`)

              // Also update org status to ACTIVE if needed
              await adminClient
                .from('employer_organizations')
                .update({
                  status: 'ACTIVE',
                  updatedAt: new Date().toISOString()
                })
                .eq('id', invite.orgId)
                .eq('status', 'PROSPECT')
            }
          }
        }

        // Now check employer_org_users table - userId must have a record
        const { data, error } = await adminClient
          .from('employer_org_users')
          .select('id, orgId, role, acceptedAt')
          .eq('userId', userId)
          .limit(1)

        if (error) {
          console.error('[PortalAuth] Employer check error:', error)
          return {
            hasAccess: false,
            data: null,
            error: 'Error verifying employer access'
          }
        }

        if (!data || data.length === 0) {
          return {
            hasAccess: false,
            data: null,
            error: "You don't have access to the employer portal. Please contact your administrator."
          }
        }

        return { hasAccess: true, data: data[0]!, error: '' }
      }

      case 'employee': {
        // Check employer_members table for employees AND dependents (family members)
        // This includes: EMPLOYEE, SPOUSE, CHILD, OTHER_DEPENDENT
        const { data: memberData, error: memberError } = await adminClient
          .from('employer_members')
          .select(`
            id,
            orgId,
            type,
            firstName,
            lastName,
            email,
            userId,
            primaryMemberId
          `)
          .or(`userId.eq.${userId},email.eq.${email}`)
          .limit(1)

        if (memberError) {
          console.error('[PortalAuth] Member check error:', memberError)
        }

        if (!memberData || memberData.length === 0) {
          return {
            hasAccess: false,
            data: null,
            error: "You don't have access to this portal. Your employer may not have added you yet, or your coverage may have ended. Please contact your HR administrator."
          }
        }

        const member = memberData[0]!

        // Link userId to member if not already set (existing user logging in)
        if (!member.userId && member.email?.toLowerCase() === email.toLowerCase()) {
          const { error: linkError } = await adminClient
            .from('employer_members')
            .update({
              userId: userId,
              updatedAt: new Date().toISOString()
            })
            .eq('id', member.id)

          if (linkError) {
            console.error('[PortalAuth] Error linking userId to member:', linkError)
          } else {
            console.log(`[PortalAuth] Linked userId ${userId} to member ${member.id}`)
            member.userId = userId // Update local reference
          }
        }

        // Check if the employer organization is active
        const { data: orgData, error: orgError } = await adminClient
          .from('employer_organizations')
          .select('id, name, status')
          .eq('id', member.orgId)
          .maybeSingle()

        if (orgError) {
          console.error('[PortalAuth] Org check error:', orgError)
        }

        if (orgData && orgData.status !== 'ACTIVE') {
          return {
            hasAccess: false,
            data: { ...member, org: orgData },
            error: "Your employer's program is not currently active. Please contact your HR administrator."
          }
        }

        // Note: Wallet creation is handled during signup/onboarding (including existing account linking)
        // See apps/employee/app/api/auth/signup/route.ts for wallet creation logic

        return {
          hasAccess: true,
          data: { ...member, org: orgData },
          error: ''
        }
      }

      case 'therapist': {
        // Check pros table - userId or email must match
        const { data, error } = await adminClient
          .from('pros')
          .select('id, userId, email, firstName, lastName, status')
          .or(`userId.eq.${userId},email.eq.${email}`)
          .maybeSingle()

        if (error) {
          console.error('[PortalAuth] Therapist check error:', error)
          return {
            hasAccess: false,
            data: null,
            error: 'Error verifying therapist access'
          }
        }

        if (!data) {
          return {
            hasAccess: false,
            data: null,
            error: "You don't have access to the therapist portal."
          }
        }

        return { hasAccess: true, data, error: '' }
      }

      case 'client':
        // Already handled in signIn - just needs users record
        return { hasAccess: true, data: null, error: '' }

      default:
        return {
          hasAccess: false,
          data: null,
          error: 'Unknown portal type'
        }
    }
  },

  /**
   * Log successful login for audit trail
   */
  async logLoginSuccess(
    adminClient: SupabaseClient,
    userId: string,
    email: string,
    portal: PortalType,
    ipAddress?: string
  ): Promise<void> {
    try {
      await auditService.log({
        userId,
        action: 'auth.login',
        resource: 'portal',
        ipAddress,
        details: {
          email,
          portal,
          timestamp: new Date().toISOString()
        }
      }, adminClient)
    } catch (error) {
      console.error('[PortalAuth] Failed to log login success:', error)
    }
  },

  /**
   * Log failed login for audit trail
   */
  async logLoginFailed(
    adminClient: SupabaseClient,
    email: string,
    portal: PortalType,
    reason: string,
    ipAddress?: string
  ): Promise<void> {
    try {
      await auditService.log({
        action: 'auth.login_failed',
        resource: 'portal',
        ipAddress,
        accessDenied: true,
        accessReason: reason,
        details: {
          email,
          portal,
          reason,
          timestamp: new Date().toISOString()
        }
      }, adminClient)
    } catch (error) {
      console.error('[PortalAuth] Failed to log login failure:', error)
    }
  }
}
